import { createContext, type ReactElement, type ReactNode, useContext, useMemo, useState } from "react"

export type LibraryLayout = "rail" | "grid"

/** The things worth changing in front of an audience. */
export interface PocOptions {
  readonly layout: LibraryLayout
  readonly dataSources: boolean
  /**
   * Whether cards wear their audience tone — the coloured block behind the
   * artwork and the header laid over it. Off strips both, leaving the covers on
   * the page background.
   *
   * Off by default. The quiet page is the one to open on: colour-coding is a
   * taxonomy the room has to be taught before it means anything, which is why
   * turning this on also puts its key on the page (`CardColorLegend`).
   */
  readonly cardColor: boolean
}

export interface PocOptionsState extends PocOptions {
  readonly setOption: <K extends keyof PocOptions>(key: K, value: PocOptions[K]) => void
}

const DEFAULTS: PocOptions = { layout: "rail", dataSources: false, cardColor: false }

const STORAGE_KEY = "poc-options"

const LAYOUTS: readonly LibraryLayout[] = ["rail", "grid"]

const isLayout = (value: unknown): value is LibraryLayout => LAYOUTS.includes(value as LibraryLayout)

/**
 * Anything unrecognised falls back to the default rather than being trusted:
 * this key is shared with whatever an earlier build of the panel wrote, and a
 * junk value would otherwise render a page nobody chose.
 */
const load = (): PocOptions => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)

    if (!raw) {
      return DEFAULTS
    }

    const stored = JSON.parse(raw) as Partial<Record<keyof PocOptions, unknown>>

    return {
      layout: isLayout(stored.layout) ? stored.layout : DEFAULTS.layout,
      dataSources: typeof stored.dataSources === "boolean" ? stored.dataSources : DEFAULTS.dataSources,
      cardColor: typeof stored.cardColor === "boolean" ? stored.cardColor : DEFAULTS.cardColor,
    }
  } catch {
    return DEFAULTS
  }
}

const PocOptionsContext = createContext<PocOptionsState | undefined>(undefined)

interface PocOptionsProviderProps {
  readonly children: ReactNode
  /**
   * Pins options at mount, over whatever storage held. For a test that needs one
   * mode in particular — a card's header icons only exist with `cardColor` on —
   * so it can say so outright rather than by seeding `localStorage`.
   */
  readonly initial?: Partial<PocOptions>
}

export const PocOptionsProvider = ({ children, initial }: PocOptionsProviderProps): ReactElement => {
  const [options, setOptions] = useState<PocOptions>(() => ({ ...load(), ...initial }))

  const value = useMemo<PocOptionsState>(
    () => ({
      ...options,
      setOption: (key, next) => {
        setOptions(current => {
          const updated = { ...current, [key]: next }

          try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(updated))
          } catch {
            /* A private window refuses storage; the switch still works for this view. */
          }

          return updated
        })
      },
    }),
    [options],
  )

  return <PocOptionsContext.Provider value={value}>{children}</PocOptionsContext.Provider>
}

/** Defaults outside a provider, so a component can be rendered on its own in a test. */
export const usePocOptions = (): PocOptionsState =>
  useContext(PocOptionsContext) ?? { ...DEFAULTS, setOption: () => undefined }
